import * as React from 'react';
// Import hooks.
import {
	useSubformPropertyInfo,
	UseSubformPropertyInfoParameters,
	UseSubformPropertyInfoResults
} from '@app/hooks/view';
import {
	useInputDataSelectionList,
	UseInputDataSelectionListResults,
	useInputNumber,
	UseInputNumberResults,
	useInputYesNo,
	UseInputYesNoResults,
} from '@app/hooks/view/input';
// Import 
import {
	PropertyDomainValuesPersonalProps,
	SubformDwellingInfoProps,
	SubformInputSettingsDwellingInfoProps,
} from '@app/props';
// Import enumerations.
import {
	BuildingFoundationTypeDetail,
} from '@app/enumerations';
// Import api models.
import {
	BuildingRiskDataPersonal,
} from '@app/models/api';

interface UseSubformDwellingInfoParameters extends UseSubformPropertyInfoParameters<PropertyDomainValuesPersonalProps, SubformInputSettingsDwellingInfoProps, BuildingRiskDataPersonal> {
	hasEarthquakePeril: boolean;
}

interface UseSubformDwellingInfoResults {
	areAllInputsDisabled: boolean;
	isYearOfLastRoofUpdateQuestionRequired: boolean;
	isBelowGradeOrEnclosedAreaFinishedQuestionRequired: boolean;
	propertyInfo: UseSubformPropertyInfoResults;
	belowGradeOrEnclosedAreaFinishedInput: UseInputYesNoResults;
	garageAttachedInput: UseInputYesNoResults;
	occupancyTypeInput: UseInputDataSelectionListResults;
	yearRoofLastUpdatedInput: UseInputNumberResults;
	applySubformDwellingInfoProps: () => SubformDwellingInfoProps;
	createBuildingRiskDataPersonal: () => BuildingRiskDataPersonal;
	flagAllInputsToShowValidations: () => void;
	doInputsHaveChanges: () => boolean;
}

const useSubformDwellingInfo = (params: UseSubformDwellingInfoParameters): UseSubformDwellingInfoResults => {

	// #region exported methods

	const applySubformDwellingInfoProps = (): SubformDwellingInfoProps => {
		return {
			...propertyInfo.applySubformPropertyInfoProps(),
			isBelowGradeOrEnclosedAreaFinishedQuestionRequired,
			isYearOfLastRoofUpdateQuestionRequired,
			showBelowGradeOrEnclosedAreaFinishedInput,
			showGarageAttachedInput,
			showOccupancyTypeInput,
			showYearRoofLastUpdatedInput,
			belowGradeOrEnclosedAreaFinishedInput,
			garageAttachedInput,
			occupancyTypeInput,
			yearRoofLastUpdatedInput,
		};
	}

	const createBuildingRiskDataPersonal = (): BuildingRiskDataPersonal => {
		let buildingRiskDataPersonal: BuildingRiskDataPersonal = {
			...propertyInfo.createPropertyInfoObject(),
			occupancyType: occupancyTypeInput.value ?? null,
			isBelowGradeOrEnclosedAreaFinished: belowGradeOrEnclosedAreaFinishedInput.value ?? null,
			isGarageAttached: garageAttachedInput.value ?? null,
			yearRoofLastUpdated: yearRoofLastUpdatedInput.value ?? null,
		}

		return buildingRiskDataPersonal;
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		propertyInfo.flagAllInputsToShowValidations();
		belowGradeOrEnclosedAreaFinishedInput.changeShowValidationFlag(true);
		occupancyTypeInput.changeShowValidationFlag(true);
		yearRoofLastUpdatedInput.changeShowValidationFlag(true);
	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doInputsHaveChanges = (): boolean => {
		return propertyInfo.doInputsHaveChanges()
			|| occupancyTypeInput.doesInputHaveChanges
			|| belowGradeOrEnclosedAreaFinishedInput.doesInputHaveChanges
			|| garageAttachedInput.doesInputHaveChanges
			|| yearRoofLastUpdatedInput.doesInputHaveChanges;
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Checks to see if the 'below grade or enclosed area finished' question is required with the selected foundation type detail provided. 
	 **/
	const isBelowGradeOrEnclosedAreaFinishedQuestionRequiredWithSelectedFoundationTypeDetail = (selectedFoundationTypeDetail: string | undefined): boolean => {
		return params.hasFloodPeril
			&& (selectedFoundationTypeDetail === BuildingFoundationTypeDetail.CRAWL_SPACE_PARTIAL_ENCLOSURE
				|| selectedFoundationTypeDetail === BuildingFoundationTypeDetail.CRAWL_SPACE_WITHOUT_ENCLOSURE
				|| selectedFoundationTypeDetail === BuildingFoundationTypeDetail.CRAWL_SPACE_WITH_ENCLOSURE
				|| selectedFoundationTypeDetail === BuildingFoundationTypeDetail.ELEVATED_FLOOR_PARTIAL_ENCLOSURE
				|| selectedFoundationTypeDetail === BuildingFoundationTypeDetail.ELEVATED_FLOOR_WITHOUT_ENCLOSURE
				|| selectedFoundationTypeDetail === BuildingFoundationTypeDetail.ELEVATED_FLOOR_WITH_ENCLOSURE
				|| selectedFoundationTypeDetail === BuildingFoundationTypeDetail.FULL_BASEMENT
				|| selectedFoundationTypeDetail === BuildingFoundationTypeDetail.SUBGRADE_CRAWL_SPACE
				|| selectedFoundationTypeDetail === BuildingFoundationTypeDetail.WALKOUT_BASEMENT);
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generate id for the occupancy type. 
	 **/
	const generateIdForOccupancyType = (): string => {
		return `${generateIdForForm()}-occupancyType`;
	}

	/**
	 * Generate id for the below grade or enclosed area finished. 
	 **/
	const generateIdForBelowGradeOrEnclosedAreaFinished = (): string => {
		return `${generateIdForForm()}-belowGradeOrEnclosedAreaFinished`;
	}

	/**
	 * Generate id for the garage attached.
	 */
	const generateIdForGarageAttached = (): string => {
		return `${generateIdForForm()}-garageAttached`;
	}

	/**
	 * Generate id for year roof last updated. 
	 **/
	const generateIdForYearRoofLastUpdated = (): string => {
		return `${generateIdForForm()}-yearRoofLastUpdated`;
	}

	//#endregion id generators

	// #region input initializers

	/**
	 * Sets up the intial value of the 'year roof last updated' input.
	 * @param initialYearRoofLastUpdatedValue
	 */
	const setupInitialYearRoofLastUpdatedValue = (initialYearRoofLastUpdatedValue: number | undefined): number | undefined => {
		return isYearOfLastRoofUpdateQuestionRequired ? initialYearRoofLastUpdatedValue : undefined;
	}

	// #endregion input initializers

	// #endregion private methods


	// #region variables

	// Setup the basic property info hook.
	const propertyInfo: UseSubformPropertyInfoResults = useSubformPropertyInfo({
		...params,
		id: generateIdForForm(),
	});

	// Cached Variables.
	const showValidation: boolean = React.useMemo(() => params.showValidationsOnMount ?? false, [params.showValidationsOnMount]);
	const isYearOfLastRoofUpdateQuestionRequired: boolean = React.useMemo(() => params.hasEarthquakePeril, [params.hasEarthquakePeril]);

	// Check to see if initially the below grade or enclosed area is required.
	let isBelowGradeOrEnclosedAreaFinishedQuestionRequired: boolean = React.useMemo(() =>
		isBelowGradeOrEnclosedAreaFinishedQuestionRequiredWithSelectedFoundationTypeDetail(propertyInfo.foundationType.foundationTypeDetailSelectionListInput.value),
		[propertyInfo.foundationType.foundationTypeDetailSelectionListInput.value]
	);

	// #region Input settings visibility.

	const showBelowGradeOrEnclosedAreaFinishedInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.belowGradeOrEnclosedAreaFinishedInput !== 'hidden',
		[params.inputSettingsProps?.belowGradeOrEnclosedAreaFinishedInput]
	);
	const showGarageAttachedInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.garageAttachedInput !== 'hidden',
		[params.inputSettingsProps?.garageAttachedInput]
	);
	const showOccupancyTypeInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.occupancyTypeInput !== 'hidden',
		[params.inputSettingsProps?.occupancyTypeInput]
	);
	const showYearRoofLastUpdatedInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.yearRoofLastUpdatedInput !== 'hidden',
		[params.inputSettingsProps?.yearRoofLastUpdatedInput]
	);

	// #endregion Input settings visibility.

	// #region Input settings disable.

	const isBelowGradeOrEnclosedAreaFinishedInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !isBelowGradeOrEnclosedAreaFinishedQuestionRequired
		|| !showBelowGradeOrEnclosedAreaFinishedInput
		|| params.inputSettingsProps?.belowGradeOrEnclosedAreaFinishedInput === 'disabled',
		[
			params.allowEdits,
			isBelowGradeOrEnclosedAreaFinishedQuestionRequired,
			showBelowGradeOrEnclosedAreaFinishedInput,
			params.inputSettingsProps?.belowGradeOrEnclosedAreaFinishedInput
		]
	);

	const isGarageAttachedInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showGarageAttachedInput
		|| params.inputSettingsProps?.garageAttachedInput === 'disabled',
		[
			params.allowEdits,
			showGarageAttachedInput,
			params.inputSettingsProps?.garageAttachedInput
		]
	);

	const isOccupancyTypeInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showOccupancyTypeInput
		|| params.inputSettingsProps?.occupancyTypeInput === 'disabled',
		[
			params.allowEdits,
			showOccupancyTypeInput,
			params.inputSettingsProps?.occupancyTypeInput
		]
	);

	const isYearRoofLastUpdatedInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !isYearOfLastRoofUpdateQuestionRequired
		|| !showYearRoofLastUpdatedInput
		|| params.inputSettingsProps?.yearRoofLastUpdatedInput === 'disabled',
		[
			params.allowEdits,
			isYearOfLastRoofUpdateQuestionRequired,
			showYearRoofLastUpdatedInput,
			params.inputSettingsProps?.yearRoofLastUpdatedInput
		]
	);

	// #endregion Input settings disable.

	// Instantiate the 'below grade or enclosed area finished' input.
	const belowGradeOrEnclosedAreaFinishedInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForBelowGradeOrEnclosedAreaFinished(),
		label: 'Is the basement or enclosed area finished?',
		required: true,
		disabled: isBelowGradeOrEnclosedAreaFinishedInputDisabled,
		isYesChecked: isBelowGradeOrEnclosedAreaFinishedQuestionRequired
			? (params.propertyInfo?.isBelowGradeOrEnclosedAreaFinished ?? undefined)
			: undefined,
		showValidation,
	});

	// Setup the 'garage attached' input hook.
	const garageAttachedInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForGarageAttached(),
		label: 'Is there a garage attached to the building?',
		required: false,
		disabled: isGarageAttachedInputDisabled,
		isYesChecked: params.propertyInfo?.isGarageAttached ?? undefined,
		showValidation,
	});

	// Setup the 'occupancy type' input hook.
	const occupancyTypeInput: UseInputDataSelectionListResults = useInputDataSelectionList({
		id: generateIdForOccupancyType(),
		label: 'Building Occupancy Type',
		required: true,
		disabled: isOccupancyTypeInputDisabled,
		value: params.propertyInfo?.occupancyType ?? undefined,
		possibleDataSelections: params.propertyDomainValuesProps.possibleOccupancyTypes,
		showValidation,
	});

	// Setup the 'year roof last updated' input hook.
	const yearRoofLastUpdatedInput: UseInputNumberResults = useInputNumber({
		id: generateIdForYearRoofLastUpdated(),
		label: 'Year The Roof Was Last Updated',
		required: true,
		disabled: isYearRoofLastUpdatedInputDisabled,
		value: setupInitialYearRoofLastUpdatedValue(params.propertyInfo?.yearRoofLastUpdated ?? undefined),
		minValue: 0,
		showValidation,
	});

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return propertyInfo.areAllInputsDisabled
			&& belowGradeOrEnclosedAreaFinishedInput.isDisabled
			&& garageAttachedInput.isDisabled
			&& occupancyTypeInput.isDisabled
			&& yearRoofLastUpdatedInput.isDisabled
	}, [
		propertyInfo.areAllInputsDisabled,
		belowGradeOrEnclosedAreaFinishedInput.isDisabled,
		garageAttachedInput.isDisabled,
		occupancyTypeInput.isDisabled,
		yearRoofLastUpdatedInput.isDisabled,
	]);

	// #endregion variables


	// #region life-cycle methods.

	// Called when the foundation type detail is updated.
	React.useEffect(() => {
		if (propertyInfo.foundationType.foundationTypeDetailSelectionListInput.doesInputHaveChanges) {
			// Get the current selected foundation type detail.
			let selectedFoundationTypeDetail: string | undefined = propertyInfo.foundationType.foundationTypeDetailSelectionListInput.value;
			// If the below grade or enclosed area finished question is not required, then reset the answer.
			if (!isBelowGradeOrEnclosedAreaFinishedQuestionRequiredWithSelectedFoundationTypeDetail(selectedFoundationTypeDetail)) {
				belowGradeOrEnclosedAreaFinishedInput.setInput(undefined);
			}
		}
	}, [propertyInfo.foundationType.foundationTypeDetailSelectionListInput.value]);

	// #endregion life-cycle methods.

	return {
		areAllInputsDisabled,
		isYearOfLastRoofUpdateQuestionRequired,
		isBelowGradeOrEnclosedAreaFinishedQuestionRequired,
		propertyInfo,
		belowGradeOrEnclosedAreaFinishedInput,
		garageAttachedInput,
		occupancyTypeInput,
		yearRoofLastUpdatedInput,
		applySubformDwellingInfoProps,
		createBuildingRiskDataPersonal,
		flagAllInputsToShowValidations,
		doInputsHaveChanges,
	};
}

// Export parameters.
export {
	UseSubformDwellingInfoParameters,
}

// Export results.
export {
	UseSubformDwellingInfoResults,
}

export default useSubformDwellingInfo;